import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Loader2, Link2, Search, Sparkles, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { ficEiStatusEliminabile } from "../utils/fatturazioneSdi";
import { cfIntestatarioDaFascicolo } from "../utils/destinatarioFatturazione";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatEuro(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `€ ${v.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** CF per ricerca FIC: priorità proposte API (intestatario), poi fascicolo. */
function resolveCfPerRicerca(fascicoloId, proposteData, fascicoli) {
  if (!fascicoloId) return "";
  const fromProposte = (proposteData?.proposte || []).find((p) => p.fascicolo_id === fascicoloId);
  const fromApi = String(fromProposte?.cf_intestatario || "").trim().toUpperCase();
  if (fromApi.length >= 11) return fromApi;
  const fa = (fascicoli || []).find((f) => f.id === fascicoloId);
  return cfIntestatarioDaFascicolo(fa);
}

export default function CollegaFicDialog({
  open,
  onOpenChange,
  headers,
  meseControllo,
  strutturaIdForConfig,
  fascicoli = [],
  initialFascicoloId = null,
  onSuccess,
}) {
  const [tab, setTab] = useState("proposte");
  const [loadingProposte, setLoadingProposte] = useState(false);
  const [proposteData, setProposteData] = useState(null);
  const [linkingId, setLinkingId] = useState(null);

  const [searchCf, setSearchCf] = useState("");
  const [searchNumero, setSearchNumero] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [manualFascicoloId, setManualFascicoloId] = useState("");
  const [deletingFicId, setDeletingFicId] = useState(null);

  const configParams = useMemo(
    () => (strutturaIdForConfig ? { strutturaId: strutturaIdForConfig } : {}),
    [strutturaIdForConfig],
  );

  const loadProposte = useCallback(async () => {
    if (!meseControllo) return;
    setLoadingProposte(true);
    try {
      const res = await axios.get(`${API_URL}/api/fatturazione/controllo-mensile/proposte-fic`, {
        headers,
        params: { mese: meseControllo, ...configParams },
      });
      setProposteData(res.data);
      if (!res.data?.success && res.data?.message) {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento proposte FIC");
      setProposteData(null);
    } finally {
      setLoadingProposte(false);
    }
  }, [headers, meseControllo, configParams]);

  useEffect(() => {
    if (!open) return;
    setTab("proposte");
    setManualFascicoloId(initialFascicoloId || "");
    if (initialFascicoloId) {
      setSearchCf(resolveCfPerRicerca(initialFascicoloId, null, fascicoli));
    } else {
      setSearchCf("");
    }
    loadProposte();
  }, [open, initialFascicoloId, loadProposte]); // eslint-disable-line react-hooks/exhaustive-deps -- fascicoli usato solo per CF iniziale all'apertura

  // Dopo le proposte API, usa il CF intestatario (non quello dell'ospite).
  useEffect(() => {
    if (!open || !manualFascicoloId || !proposteData) return;
    const cf = resolveCfPerRicerca(manualFascicoloId, proposteData, fascicoli);
    if (cf) setSearchCf(cf);
  }, [open, manualFascicoloId, proposteData, fascicoli]);

  const handleCollega = async (fascicoloId, ficDocumentId) => {
    if (!fascicoloId || !ficDocumentId) return;
    setLinkingId(`${fascicoloId}-${ficDocumentId}`);
    try {
      const res = await axios.post(
        `${API_URL}/api/fatturazione/collega-fattura-fic`,
        {
          fascicolo_id: fascicoloId,
          mese: meseControllo,
          fic_document_id: Number(ficDocumentId),
          ...(strutturaIdForConfig ? { strutturaId: strutturaIdForConfig } : {}),
        },
        { headers },
      );
      toast.success(res.data?.message || "Fattura collegata da FIC");
      if (res.data?.avvisi?.length) {
        res.data.avvisi.forEach((a) => toast.warning(a));
      }
      onSuccess?.(res.data);
      loadProposte();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Collegamento non riuscito");
    } finally {
      setLinkingId(null);
    }
  };

  const handleSearch = async () => {
    setSearchLoading(true);
    try {
      const params = { mese: meseControllo, ...configParams };
      if (searchCf.trim()) params.cf = searchCf.trim();
      if (searchNumero.trim()) params.numero = parseInt(searchNumero, 10);
      const res = await axios.get(`${API_URL}/api/fatturazione/cerca-fatture-fic`, {
        headers,
        params,
      });
      setSearchResults(res.data?.items || []);
      if (!res.data?.items?.length) {
        toast.info("Nessuna fattura FIC trovata per i filtri indicati");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ricerca non riuscita");
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleEliminaFic = async (item) => {
    if (!item?.fic_document_id) return;
    if (!window.confirm(`Eliminare FIC n. ${item.numero} su Fatture in Cloud${item.gia_collegata ? " e il record collegato in Affidya" : ""}?`)) {
      return;
    }
    setDeletingFicId(item.fic_document_id);
    try {
      const res = await axios.delete(
        `${API_URL}/api/fatturazione/fatture-attive/${item.fic_document_id}`,
        { headers, params: configParams },
      );
      toast.success(res.data?.message || "Fattura eliminata su FIC");
      setSearchResults((prev) => prev.filter((r) => r.fic_document_id !== item.fic_document_id));
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eliminazione FIC non riuscita");
    } finally {
      setDeletingFicId(null);
    }
  };

  const mancantiOptions = useMemo(() => {
    const fromProposte = (proposteData?.proposte || []).map((p) => ({
      id: p.fascicolo_id,
      label: p.ospite,
    }));
    if (initialFascicoloId && !fromProposte.some((o) => o.id === initialFascicoloId)) {
      const fa = fascicoli.find((f) => f.id === initialFascicoloId);
      const label =
        fa?.anagrafica?.nome_completo ||
        [fa?.anagrafica?.nome, fa?.anagrafica?.cognome].filter(Boolean).join(" ") ||
        initialFascicoloId;
      return [{ id: initialFascicoloId, label }, ...fromProposte];
    }
    return fromProposte;
  }, [proposteData, initialFascicoloId, fascicoli]);

  const proposteFiltrate = initialFascicoloId
    ? (proposteData?.proposte || []).filter((p) => p.fascicolo_id === initialFascicoloId)
    : proposteData?.proposte || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-sky-600" />
            Collega fattura da Fatture in Cloud
          </DialogTitle>
          <DialogDescription>
            Collega fatture già emesse su FIC agli ospiti mancanti su Affidya per il mese{" "}
            <strong>{meseControllo}</strong>. Non verrà creata una nuova fattura su FIC.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 border-b pb-2">
          <Button
            type="button"
            size="sm"
            variant={tab === "proposte" ? "default" : "outline"}
            className={tab === "proposte" ? "bg-sky-600 hover:bg-sky-700" : ""}
            onClick={() => setTab("proposte")}
          >
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            Proposte automatiche
          </Button>
          <Button
            type="button"
            size="sm"
            variant={tab === "ricerca" ? "default" : "outline"}
            className={tab === "ricerca" ? "bg-sky-600 hover:bg-sky-700" : ""}
            onClick={() => setTab("ricerca")}
          >
            <Search className="w-3.5 h-3.5 mr-1.5" />
            Ricerca manuale
          </Button>
        </div>

        {tab === "proposte" && (
          <div className="space-y-4">
            {loadingProposte ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : proposteFiltrate.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">
                Nessuna proposta automatica per i mancanti di questo mese. Prova la ricerca manuale.
              </p>
            ) : (
              proposteFiltrate.map((row) => (
                <div
                  key={row.fascicolo_id}
                  className="rounded-lg border border-slate-200 p-3 space-y-2"
                >
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <p className="font-medium text-slate-900">{row.ospite}</p>
                      <p className="text-xs text-slate-500">
                        Previsto: {formatEuro(row.totale_previsto)}
                        {row.cf_intestatario ? (
                          <> · CF intestatario {row.cf_intestatario}</>
                        ) : null}
                      </p>
                    </div>
                  </div>
                  {row.proposte?.length ? (
                    <ul className="space-y-2">
                      {row.proposte.map((p) => (
                        <li
                          key={p.fic_document_id}
                          className="flex flex-wrap items-center gap-2 rounded-md bg-slate-50 px-3 py-2 text-sm"
                        >
                          <div className="flex-1 min-w-[180px]">
                            <span className="font-medium">FIC n. {p.numero}</span>
                            <span className="text-slate-500 ml-2">{p.data}</span>
                            <p className="text-xs text-slate-600 mt-0.5">
                              {p.destinatario} · {formatEuro(p.totale)}
                            </p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              <Badge variant="outline" className="text-[10px]">
                                Score {p.score}
                              </Badge>
                              {(p.motivi || []).map((m) => (
                                <Badge key={m} className="text-[10px] bg-sky-100 text-sky-800">
                                  {m}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            className="bg-sky-600 hover:bg-sky-700"
                            disabled={linkingId === `${row.fascicolo_id}-${p.fic_document_id}`}
                            onClick={() => handleCollega(row.fascicolo_id, p.fic_document_id)}
                          >
                            {linkingId === `${row.fascicolo_id}-${p.fic_document_id}` ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <>
                                <Link2 className="w-3.5 h-3.5 mr-1" />
                                Collega
                              </>
                            )}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-amber-700">Nessun match FIC con score sufficiente.</p>
                  )}
                </div>
              ))
            )}
            {proposteData?.fic_totali_periodo != null && (
              <p className="text-xs text-slate-400 text-center">
                {proposteData.fic_totali_periodo} fatture FIC nel periodo analizzato
              </p>
            )}
          </div>
        )}

        {tab === "ricerca" && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Codice fiscale intestatario (destinatario FIC)</Label>
                <Input
                  value={searchCf}
                  onChange={(e) => setSearchCf(e.target.value)}
                  placeholder="RSSMRA80A01H501U"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Numero fattura FIC</Label>
                <Input
                  value={searchNumero}
                  onChange={(e) => setSearchNumero(e.target.value)}
                  placeholder="42"
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Ospite mancante da collegare</Label>
              <Select
                value={manualFascicoloId || ""}
                onValueChange={(id) => {
                  setManualFascicoloId(id);
                  const cf = resolveCfPerRicerca(id, proposteData, fascicoli);
                  if (cf) setSearchCf(cf);
                }}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona ospite" />
                </SelectTrigger>
                <SelectContent>
                  {mancantiOptions.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={handleSearch}
              disabled={searchLoading}
            >
              {searchLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              Cerca su FIC
            </Button>

            <ul className="space-y-2 max-h-64 overflow-y-auto">
              {searchResults.map((item) => (
                <li
                  key={item.fic_document_id}
                  className={cn(
                    "flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm",
                    item.gia_collegata ? "opacity-60 bg-slate-50" : "border-slate-200",
                  )}
                >
                  <div className="flex-1 min-w-[160px]">
                    <span className="font-medium">FIC n. {item.numero}</span>
                    <span className="text-slate-500 ml-2">{item.data}</span>
                    <p className="text-xs text-slate-600">
                      {item.destinatario} · CF {item.destinatario_cf || "—"} ·{" "}
                      {formatEuro(item.totale)}
                    </p>
                    {item.gia_collegata && (
                      <Badge className="text-[10px] mt-1 bg-slate-200 text-slate-700">
                        Già collegata
                        {item.fattura_affidya_numero
                          ? ` · Affidya ${item.fattura_affidya_numero}`
                          : ""}
                      </Badge>
                    )}
                    {item.gia_collegata && item.fattura_affidya_id && (
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Apri {item.fattura_affidya_numero || "la fattura"} nel modulo Fatturazione
                        {item.fattura_affidya_mese ? ` (mese ${item.fattura_affidya_mese})` : ""}.
                        Se è stornata, scollega FIC o collega un nuovo documento.
                      </p>
                    )}
                  </div>
                  {!item.gia_collegata && (
                    <Button
                      type="button"
                      size="sm"
                      disabled={!manualFascicoloId || linkingId === `${manualFascicoloId}-${item.fic_document_id}`}
                      onClick={() => handleCollega(manualFascicoloId, item.fic_document_id)}
                    >
                      {linkingId === `${manualFascicoloId}-${item.fic_document_id}` ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        "Collega"
                      )}
                    </Button>
                  )}
                  {item.gia_collegata && ficEiStatusEliminabile(item.ei_status) && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="text-red-700 border-red-200 hover:bg-red-50"
                      disabled={deletingFicId === item.fic_document_id}
                      onClick={() => handleEliminaFic(item)}
                    >
                      {deletingFicId === item.fic_document_id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <>
                          <Trash2 className="w-3.5 h-3.5 mr-1" />
                          Elimina su FIC
                        </>
                      )}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
